/**
 * Wraps a function so repeated calls collapse into a single call after `delay`
 * ms of silence, resetting the timer on every invocation. Useful for things
 * like live search filtering, where you want to react to typing without
 * re-running expensive work (e.g. re-rendering a large table) on every keystroke.
 *
 * Since this codebase's React version predates hooks-friendly patterns in
 * class components, this is a plain closure-based debounce (no `useCallback`/
 * `useMemo`) so it works the same in class components and function components.
 *
 * @template {(...args: any[]) => void} T
 * @param {T} fn - The function to debounce.
 * @param {number} delay - Milliseconds to wait after the last call before invoking `fn`.
 * @returns {T & { cancel: () => void }} A debounced wrapper around `fn`, plus a
 *   `cancel()` method that clears any pending call (call this in
 *   `componentWillUnmount`/a cleanup effect to avoid setting state after unmount).
 *
 * @example
 * // In a class component:
 * this.debouncedSetSearchText = debounce((text) => this.setState({ debouncedSearchText: text }), 200);
 * // On every keystroke:
 * this.debouncedSetSearchText(event.target.value);
 * // On unmount:
 * this.debouncedSetSearchText.cancel();
 */
export const debounce = (fn, delay) => {
    let timer = null;

    const debounced = (...args) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            timer = null;
            fn(...args);
        }, delay);
    };

    debounced.cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    return debounced;
};
